/**
 * CSRF Token Utilities
 *
 * Helper functions for CSRF protection when using fetch API directly
 * (CommHandler/axios automatically handles CSRF tokens)
 */

/**
 * Get CSRF token from cookie
 * @returns CSRF token string or null if not found
 */
export const getCsrfToken = (): string | null => {
  const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
  if (!match) {
    return null;
  }

  const rawToken = match[1];

  try {
    // Decode the token in case it contains URL-encoded characters
    return decodeURIComponent(rawToken);
  } catch (error) {
    // If decoding fails, fall back to the raw token to avoid breaking existing behavior
    return rawToken;
  }
};

/**
 * Check if CSRF protection is enabled
 * @returns true if CSRF is enabled via environment variable
 */
export const isCsrfEnabled = (): boolean => {
  return process.env.REACT_APP_ENABLE_CSRF === 'true';
};

/**
 * Get headers object with CSRF token included (if available)
 * Use this helper when making fetch requests directly
 *
 * @param additionalHeaders - Any additional headers to include
 * @returns Headers object with CSRF token
 *
 * @example
 * ```typescript
 * const headers = getCsrfHeaders({
 *   'Content-Type': 'application/json',
 *   'Authorization': 'Bearer token'
 * });
 *
 * fetch('/api/endpoint', {
 *   method: 'POST',
 *   headers,
 *   credentials: 'include',
 *   body: JSON.stringify(data)
 * });
 * ```
 */
export const getCsrfHeaders = (additionalHeaders: Record<string, string> = {}): Record<string, string> => {
  const csrfToken = getCsrfToken();

  return {
    ...additionalHeaders,
    ...(csrfToken && { 'x-xsrf-token': csrfToken }),
  };
};

/**
 * Create fetch options with CSRF protection
 * Use this helper for POST/PUT/DELETE/PATCH requests with fetch API
 *
 * @param options - Standard fetch options
 * @returns Fetch options with CSRF token and credentials
 *
 * @example
 * ```typescript
 * const response = await fetch('/api/users', createCsrfFetchOptions({
 *   method: 'POST',
 *   headers: { 'Content-Type': 'application/json' },
 *   body: JSON.stringify(userData)
 * }));
 * ```
 */
export const createCsrfFetchOptions = (options: RequestInit = {}): RequestInit => {
  const csrfToken = getCsrfToken();
  const { headers, ...restOptions } = options;

  const normalizedHeaders: Record<string, string> = {};

  // Normalize different HeadersInit formats into a plain object
  if (headers instanceof Headers) {
    headers.forEach((value, key) => {
      normalizedHeaders[key] = value;
    });
  } else if (Array.isArray(headers)) {
    headers.forEach((entry) => {
      const [key, value] = entry;
      if (typeof key === 'string' && typeof value === 'string') {
        normalizedHeaders[key] = value;
      }
    });
  } else if (headers && typeof headers === 'object') {
    Object.assign(normalizedHeaders, headers as Record<string, string>);
  }

  return {
    ...restOptions,
    headers: {
      ...normalizedHeaders,
      ...(csrfToken && { 'x-xsrf-token': csrfToken }),
    },
    credentials: 'include', // Always include credentials for CSRF
  };
};
